import { memo, useCallback, useMemo, useState } from 'react';

import ConfiguratorTabContainer from '../ConfiguratorTab/ConfiguratorTabContainer';
import styles from './Configurator.module.css';

import type { FiltratorId } from '@Stores/Filtrator';
import type { HTMLAttributes } from 'react';
import type {
  ConfiguratorData,
  ConstructorTabData,
  ConstructorTabItemData,
  ConstructorValueData,
} from '@Pages/PageProduct/typings';

export interface ConfiguratorProps extends HTMLAttributes<HTMLDivElement> {
  configurator: ConfiguratorData;
  className?: string;
  entityId: FiltratorId;
}

interface ConfiguratorTabItemData extends ConstructorTabItemData {
  values: ConstructorValueData[];
}

export interface ConfiguratorTabData extends ConstructorTabData {
  count: number;
  items: ConfiguratorTabItemData[];
}

const Configurator = (props: ConfiguratorProps) => {
  const { configurator, entityId, ...restProps } = props;
  const configuratorTabs = useMemo<ConfiguratorTabData[]>(() => {
    return configurator.tabs
      .map((tab) => {
        const itemsWithValues = tab.items.map((item) => ({
          ...item,
          values: configurator.parameterValues.filter((pv) =>
            item.parameterIds.includes(pv.parameterIds[0]),
          ),
        }));

        const totalCount = itemsWithValues.reduce(
          (acc, item) => acc + item.values.filter((val) => !val.hidden).length,
          0,
        );

        return { ...tab, items: itemsWithValues, count: totalCount };
      })
      .filter((tab) => tab.items.some((item) => item.values.length >= 2));
  }, [configurator.tabs, configurator.parameterValues]);

  const [activeTabs, setActiveTabs] = useState([configuratorTabs[0]]);

  const handleClickTab = useCallback((tab: ConfiguratorTabData) => {
    setActiveTabs((prev) => {
      // Проверяем, есть ли элемент с таким же id в prev
      const exists = prev.some((existingTab) => existingTab.id === tab.id);

      if (exists) {
        // Если элемент существует, удаляем его из массива
        return prev.filter((existingTab) => existingTab.id !== tab.id);
      }
      // Если элемента нет, добавляем новый элемент в массив
      return [...prev, tab];
    });
  }, []);

  return (
    <div {...restProps}>
      {configuratorTabs.map((tab) => {
        return (
          <div key={tab.id} className={styles.tab}>
            <ConfiguratorTabContainer
              tab={tab}
              configurator={configurator}
              entityId={entityId}
              isActive={activeTabs?.some((activeTab) => activeTab.id === tab.id)}
              onClickTab={handleClickTab}
            />
          </div>
        );
      })}
    </div>
  );
};

export default memo(Configurator);
